/**
 * GTM form validation with support for Gravity Forms. Checks forms for validation errors on page load
 * and upon form submission, pushing errors to the dataLayer for GTM tracking.
 * @exports
 */
export default function formFails() {
  /**
   * Collects and returns error details from invalid form elements.
   * @param {HTMLFormElement} form - The form to check for validation errors.
   * @returns {Object[]} An array of objects containing error details for each invalid form element.
   */
  const getFormErrors = (form) => {
    return [...form.elements].filter(el => el.willValidate && !el.validity.valid).map(el => ({
      name: el.name || 'Unnamed Element',
      type: el.type || 'Unknown Type',
      message: el.validationMessage
    }));
  };

  /**
   * Retrieves a form's name, falling back to the Gravity Forms title if present, or a default name.
   * @param {HTMLFormElement} form - The form to retrieve the name for.
   * @returns {string} The name of the form.
   */
  const getFormName = (form) => {
    if (form.classList.contains('gform_wrapper')) {
      const titleElement = form.querySelector('.gform_title');
      return titleElement ? titleElement.textContent.trim() : form.name || form.id || 'Unnamed Form';
    }
    return form.name || form.id || 'Unnamed Form';
  };

  /**
   * Pushes form errors to the dataLayer based on a form element.
   * @param {HTMLFormElement} form - The form element to check for errors and push to the dataLayer.
   */
  const pushToDataLayer = (form) => {
      const formName = getFormName(form); // Assumes getFormName is defined elsewhere
      const errors = getFormErrors(form); // Assumes getFormErrors is defined elsewhere
      if (errors.length > 0) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_fails',
          form_name: formName,
          form_errors: errors
        });
      }
  };

  /**
   * Checks all Gravity Forms on the page for validation errors on page load and pushes them to the dataLayer.
   */
  const checkGravityFormsErrorsOnLoad = () => {
    document.querySelectorAll('form.gform_wrapper').forEach(pushToDataLayer);
  };

  /**
   * Handles the form submission event, checks for validation errors, and prevents submission if errors are found,
   * pushing the errors to the dataLayer.
   * @param {Event} event - The form submission event.
   */
  const handleSubmit = (event) => {
    const form = event.target;
    if (!form.checkValidity()) {
      pushToDataLayer(form)
    }
  };

  // Get all forms on the page and attach the submit event listener to each form
  const forms = document.querySelectorAll('form');
  forms.forEach(form => {
    form.addEventListener('submit', handleSubmit);  
  });

  document.addEventListener('DOMContentLoaded', checkGravityFormsErrorsOnLoad);
}
